
import { useState, useEffect, useRef } from "react";
import { Input } from "../../../ui";
import { DescuentosPromocionalesProps } from "@/types";
import { safeFetch } from "@/api/safeFetch";

interface Props extends DescuentosPromocionalesProps {
  profesionalId?: number;
  perfilProfesionalId?: string;
  onMensajeChange?: (mensaje: string | null) => void;
  onCodigoValido?: (codigo: string | null) => void;
  onAfiliadoDetectado?: (afiliado: any | null) => void;
}

interface ProfesionDisponible {
  profesion_id: number;
  nombre_profesion: string;
}

interface RespuestaValidacion {
  valid?: boolean;
  message?: string;
  porcentaje_descuento?: number;
  fecha_expiracion?: string | null;
  profesion_id?: number | null;
  afiliado?: {
    id: number;
    activo: number;
  } | null;
}

const DescuentosPromocionales: React.FC<Props> = ({
  perfilProfesionalId,
  onDescuentoChange,
  onMensajeChange,
  onCodigoValido,
  onAfiliadoDetectado,
  descuentoHabilitado = false,
}) => {
  const [codigoDescuento, setCodigoDescuento] = useState("");
  const [mensaje, setMensaje] = useState<string | null>(null);
  const [profesiones, setProfesiones] = useState<ProfesionDisponible[]>([]);
  const [profesionesCargadas, setProfesionesCargadas] = useState(false);
  const [errorProfesiones, setErrorProfesiones] = useState(false);

  const lastCheckedRef = useRef<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Obtener los identificadores numéricos de las profesiones.
  useEffect(() => {
    let cancelado = false;

    const cargarProfesiones = async () => {
      try {
        const response = await safeFetch(
          "/api/profesiones/sacarProfesion/registrarse"
        );

        if (!response.ok) {
          throw new Error("No se pudieron cargar las profesiones");
        }

        const data: ProfesionDisponible[] = await response.json();

        if (!cancelado) {
          setProfesiones(data);
          setErrorProfesiones(false);
        }
      } catch (error) {
        if (!cancelado) {
          console.error("Error al cargar profesiones:", error);
          setErrorProfesiones(true);
        }
      } finally {
        if (!cancelado) {
          setProfesionesCargadas(true);
        }
      }
    };

    void cargarProfesiones();

    return () => {
      cancelado = true;
    };
  }, []);

  // Resolver el ID numérico a partir del nombre seleccionado.
  const profesionSeleccionada = profesiones.find(
    (profesion) =>
      profesion.nombre_profesion.trim().toLocaleLowerCase() ===
      (perfilProfesionalId ?? "").trim().toLocaleLowerCase()
  );

  const profesionId = profesionSeleccionada?.profesion_id;

  useEffect(() => {
    const codigo = codigoDescuento.trim().toUpperCase();

    const timeoutId = setTimeout(async () => {
      if (!codigo) {
        abortRef.current?.abort();
        lastCheckedRef.current = null;
        setMensaje(null);
        onMensajeChange?.(null);
        onDescuentoChange?.(0);
        onCodigoValido?.(null);
        onAfiliadoDetectado?.(null);
        return;
      }

      if (codigo.length < 4) {
        return;
      }

      if (!descuentoHabilitado) {
        const m = "El descuento solo está disponible para pago anual";

        setMensaje(m);
        onMensajeChange?.(m);
        onDescuentoChange?.(0);
        onCodigoValido?.(null);
        onAfiliadoDetectado?.(null);
        return;
      }

      // Esperar a tener la lista antes de validar el código.
      if (!profesionesCargadas) {
        return;
      }

      if (errorProfesiones) {
        const m = "No se pudieron cargar las profesiones";

        setMensaje(m);
        onMensajeChange?.(m);
        onDescuentoChange?.(0);
        onCodigoValido?.(null);
        onAfiliadoDetectado?.(null);
        return;
      }

      if (!profesionId) {
        const m = "No se ha podido identificar la profesión seleccionada";

        setMensaje(m);
        onMensajeChange?.(m);
        onDescuentoChange?.(0);
        onCodigoValido?.(null);
        onAfiliadoDetectado?.(null);
        return;
      }

      // Si cambia la profesión, volver a validar aunque el código sea el mismo.
      const checkKey = `${codigo}:${profesionId}`;

      if (lastCheckedRef.current === checkKey) {
        return;
      }

      lastCheckedRef.current = checkKey;

      abortRef.current?.abort();

      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const response = await safeFetch(
          "/api/codigosPromocionales/validar-profesion",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              codigo,
              profesionId,
            }),
            signal: controller.signal,
          }
        );

        const data: RespuestaValidacion = await response.json();

        if (controller.signal.aborted) {
          return;
        }

        if (!response.ok || data.valid !== true) {
          const m = data.message || "Código inválido";

          setMensaje(m);
          onMensajeChange?.(m);
          onDescuentoChange?.(0);
          onCodigoValido?.(null);
          onAfiliadoDetectado?.(null);
          return;
        }

        if (
          typeof data.porcentaje_descuento !== "number" ||
          !Number.isFinite(data.porcentaje_descuento)
        ) {
          const m = "La respuesta del código promocional no es válida";

          setMensaje(m);
          onMensajeChange?.(m);
          onDescuentoChange?.(0);
          onCodigoValido?.(null);
          onAfiliadoDetectado?.(null);
          return;
        }

        setMensaje(null);
        onMensajeChange?.(null);
        onAfiliadoDetectado?.(data.afiliado ?? null);
        onDescuentoChange?.(data.porcentaje_descuento);
        onCodigoValido?.(codigo);
      } catch (error) {
        if (
          error instanceof Error &&
          error.name === "AbortError"
        ) {
          return;
        }

        console.error("Error al validar el código promocional:", error);

        const m = "Error del servidor";

        setMensaje(m);
        onMensajeChange?.(m);
        onDescuentoChange?.(0);
        onCodigoValido?.(null);
        onAfiliadoDetectado?.(null);
      }
    }, 700);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [
    codigoDescuento,
    descuentoHabilitado,
    perfilProfesionalId,
    profesionId,
    profesionesCargadas,
    errorProfesiones,
    onDescuentoChange,
    onMensajeChange,
    onCodigoValido,
    onAfiliadoDetectado,
  ]);

  return (
    <div className="descuentos-promocionales">
      <Input
        type="text"
        placeholder={
          descuentoHabilitado
            ? "Código de descuento"
            : "Descuento solo para pago anual"
        }
        value={codigoDescuento}
        onChange={(e) => {
          setCodigoDescuento(e.target.value);

          // Permitir validar de nuevo el mismo código.
          lastCheckedRef.current = null;

          if (mensaje) {
            setMensaje(null);
            onMensajeChange?.(null);
          }
        }}
        className="w-full bg-blue-200 rounded-lg border border-blue-700 text-center"
        disabled={!descuentoHabilitado}
      />

      {mensaje && (
        <p role="alert" className="mt-2 text-sm text-red-600 text-center">
          {mensaje}
        </p>
      )}
    </div>
  );
};

export default DescuentosPromocionales;
